import React, { useState, useEffect } from 'react';
import { Plus, Calendar, DollarSign, Users, Repeat, TrendingUp } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { useOrganization } from '../hooks/useOrganization';
import { formatDate } from '../utils/dateFormat';
import { usePermissions } from '../hooks/usePermissions';

const ExpenseManagement = () => {
  const { organization } = useOrganization();
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('expenses');
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [viewingRecurringExpense, setViewingRecurringExpense] = useState(null);
  const [editingRecurringExpense, setEditingRecurringExpense] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    startDate: '',
    endDate: '',
    type: ''
  });

  useEffect(() => {
    fetchCategories();
    if (activeTab === 'expenses') fetchExpenses();
    else if (activeTab === 'recurring') fetchRecurringExpenses();
    else if (activeTab === 'payroll') fetchPayrollRecords();
  }, [activeTab, filters]);

  const fetchExpenses = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await axios.get(`/api/expenses?${params.toString()}`);
      let filteredExpenses = response.data.expenses || [];
      
      if (filters.type) {
        filteredExpenses = filteredExpenses.filter(expense => expense.expenseType === filters.type);
      }
      
      setExpenses(filteredExpenses);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/expense-categories');
      setCategories(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const fetchRecurringExpenses = async () => {
    try {
      const response = await axios.get('/api/recurring-expenses');
      setRecurringExpenses(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch recurring expenses');
    }
  };

  const fetchPayrollRecords = async () => {
    try {
      const response = await axios.get('/api/payroll');
      setPayrollRecords(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch payroll records');
    }
  };

  const handleCreateExpense = async (formData) => {
    try {
      await axios.post('/api/expenses', {
        category_id: formData.categoryId,
        cash_account_id: formData.cashAccountId || 1,
        description: formData.description,
        amount: formData.amount,
        expense_date: formData.expenseDate
      });
      toast.success('Expense created successfully');
      setShowForm(false);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create expense');
    }
  };

  const handleCreateRecurringExpense = async (formData) => {
    try {
      await axios.post('/api/recurring-expenses', {
        category_id: formData.categoryId,
        description: formData.description,
        amount: formData.amount,
        frequency: formData.frequency,
        next_due_date: formData.nextDueDate
      });
      toast.success('Recurring expense created successfully');
      setShowRecurringForm(false);
      fetchRecurringExpenses();
    } catch (error) {
      toast.error('Failed to create recurring expense');
    }
  };

  const generatePayroll = async () => {
    const monthYear = new Date().toISOString().slice(0, 7);
    try {
      setLoading(true);
      await axios.post('/api/payroll/generate', { monthYear });
      toast.success('Payroll generated successfully');
      fetchPayrollRecords();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate payroll');
    } finally {
      setLoading(false);
    }
  };

  const processRecurringExpenses = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/recurring-expenses/process');
      toast.success(response.data.message);
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to process recurring expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleViewExpense = (expense) => {
    setViewingExpense(expense);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setShowForm(true);
  };

  const handleDeleteExpense = async (expenseId) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await axios.delete(`/api/expenses/${expenseId}`);
        toast.success('Expense deleted successfully');
        fetchExpenses();
      } catch (error) {
        toast.error('Failed to delete expense');
      }
    }
  };

  const handleUpdateExpense = async (formData) => {
    try {
      await axios.put(`/api/expenses/${editingExpense.id}`, {
        category_id: formData.categoryId,
        description: formData.description,
        amount: formData.amount,
        expense_date: formData.expenseDate
      });
      toast.success('Expense updated successfully');
      setShowForm(false);
      setEditingExpense(null);
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to update expense');
    }
  };

  const handleViewRecurringExpense = (expense) => {
    setViewingRecurringExpense(expense);
  };

  const handleEditRecurringExpense = (expense) => {
    setEditingRecurringExpense(expense);
    setShowRecurringForm(true);
  };

  const handleUpdateRecurringExpense = async (formData) => {
    try {
      await axios.put(`/api/recurring-expenses/${editingRecurringExpense.id}`, {
        category_id: formData.categoryId,
        description: formData.description,
        amount: formData.amount,
        frequency: formData.frequency,
        next_due_date: formData.nextDueDate
      });
      toast.success('Recurring expense updated successfully');
      setShowRecurringForm(false);
      setEditingRecurringExpense(null);
      fetchRecurringExpenses();
    } catch (error) {
      toast.error('Failed to update recurring expense');
    }
  };

  const handleDeleteRecurringExpense = async (expenseId) => {
    if (window.confirm('Are you sure you want to delete this recurring expense?')) {
      try {
        await axios.delete(`/api/recurring-expenses/${expenseId}`);
        toast.success('Recurring expense deleted successfully');
        fetchRecurringExpenses();
      } catch (error) {
        toast.error('Failed to delete recurring expense');
      }
    }
  };

  const handleProcessIndividualExpense = async (expenseId) => {
    if (window.confirm('Process this recurring expense now?')) {
      try {
        await axios.post(`/api/recurring-expenses/${expenseId}/process`);
        toast.success('Recurring expense processed successfully');
        fetchRecurringExpenses();
        fetchExpenses();
      } catch (error) {
        toast.error('Failed to process recurring expense');
      }
    }
  };

  const ExpenseForm = ({ onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      categoryId: editingExpense?.categoryId || '',
      cashAccountId: '',
      description: editingExpense?.description || '',
      amount: editingExpense?.amount || '',
      expenseDate: editingExpense?.expenseDate ? new Date(editingExpense.expenseDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });

    const [cashAccounts, setCashAccounts] = useState([]);
    
    useEffect(() => {
      const fetchCashAccounts = async () => {
        try {
          const response = await axios.get('/api/accounts/cash');
          setCashAccounts(response.data || []);
        } catch (error) {
          setCashAccounts([]);
        }
      };
      fetchCashAccounts();
    }, []);

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">{editingExpense ? 'Edit Expense' : 'Add New Expense'}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={formData.categoryId}
              onChange={(e) => setFormData({...formData, categoryId: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cash Account</label>
            <select
              value={formData.cashAccountId}
              onChange={(e) => setFormData({...formData, cashAccountId: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select Cash Account</option>
              {cashAccounts.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <input
              type="date"
              value={formData.expenseDate}
              onChange={(e) => setFormData({...formData, expenseDate: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              {editingExpense ? 'Update Expense' : 'Create Expense'}
            </button>
            <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  const RecurringExpenseForm = ({ onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      categoryId: editingRecurringExpense?.category_id || '',
      description: editingRecurringExpense?.description || '',
      amount: editingRecurringExpense?.amount || '',
      frequency: editingRecurringExpense?.frequency || 'monthly',
      nextDueDate: editingRecurringExpense?.next_due_date ? new Date(editingRecurringExpense.next_due_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">{editingRecurringExpense ? 'Edit Recurring Expense' : 'Add Recurring Expense'}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={formData.categoryId}
              onChange={(e) => setFormData({...formData, categoryId: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Frequency</label>
            <select
              value={formData.frequency}
              onChange={(e) => setFormData({...formData, frequency: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Next Due Date</label>
            <input
              type="date"
              value={formData.nextDueDate}
              onChange={(e) => setFormData({...formData, nextDueDate: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              {editingRecurringExpense ? 'Update Recurring Expense' : 'Create Recurring Expense'}
            </button>
            <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Expense Management</h1>
        <div className="flex space-x-2">
          {activeTab === 'recurring' && (
            <div className="flex space-x-2">
              <button
                onClick={() => setShowRecurringForm(true)}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Recurring
              </button>
              <button
                onClick={processRecurringExpenses}
                disabled={loading}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                Process Due Expenses
              </button>
            </div>
          )}
          {activeTab === 'payroll' && (
            <button
              onClick={generatePayroll}
              disabled={loading}
              className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              Generate Payroll
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {[
            { id: 'expenses', name: 'Expenses', icon: DollarSign },
            { id: 'recurring', name: 'Recurring', icon: Repeat },
            { id: 'payroll', name: 'Payroll', icon: Users },
            { id: 'budget', name: 'Budget', icon: TrendingUp }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 flex items-center ${
                  activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent'
                }`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'expenses' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({...filters, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All Types</option>
                  <option value="manual">Manual</option>
                  <option value="recurring">Recurring</option>
                  <option value="accrued">Accrued</option>
                </select>
              </div>
            </div>
            <div className="mt-3 flex justify-between items-center">
              <button
                onClick={() => setFilters({ category: '', startDate: '', endDate: '', type: '' })}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
              >
                Clear Filters
              </button>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Expense
                </button>
              )}
            </div>
          </div>

          {showForm ? (
            <ExpenseForm onSubmit={editingExpense ? handleUpdateExpense : handleCreateExpense} onCancel={() => { setShowForm(false); setEditingExpense(null); }} />
          ) : (
            <div className="bg-white rounded-lg shadow border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {expenses.map(expense => (
                      <tr key={expense.id}>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {formatDate(expense.expenseDate)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{expense.description}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{expense.category?.name || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(expense.amount, organization?.currency_code)}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            expense.expenseType === 'recurring' ? 'bg-blue-100 text-blue-800' :
                            expense.expenseType === 'payroll' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {expense.expenseType}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewExpense(expense)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View"
                            >
                              👁️
                            </button>
                            {hasPermission('expenses.update') && (
                            <button
                              onClick={() => handleEditExpense(expense)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            )}
                            {hasPermission('expenses.delete') && (
                            <button
                              onClick={() => handleDeleteExpense(expense.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              🗑️
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'recurring' && (
        <div className="space-y-4">
          {showRecurringForm ? (
            <RecurringExpenseForm onSubmit={editingRecurringExpense ? handleUpdateRecurringExpense : handleCreateRecurringExpense} onCancel={() => { setShowRecurringForm(false); setEditingRecurringExpense(null); }} />
          ) : (
            <div className="bg-white rounded-lg shadow border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Due</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {recurringExpenses.map(expense => (
                      <tr key={expense.id}>
                        <td className="px-6 py-4 text-sm text-gray-900">{expense.description}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{expense.category?.name || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(expense.amount, organization?.currency_code)}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{expense.frequency}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {expense.next_due_date ? formatDate(expense.next_due_date).replace(/\s/g, '-') : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {(() => {
                            if (!expense.next_due_date) return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Unknown</span>;
                            const today = new Date();
                            const dueDate = new Date(expense.next_due_date);
                            const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                            
                            if (diffDays < 0) {
                              return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Overdue</span>;
                            } else if (diffDays === 0) {
                              return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">Due Today</span>;
                            } else if (diffDays <= 7) {
                              return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Due Soon</span>;
                            } else {
                              return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Upcoming</span>;
                            }
                          })()} 
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewRecurringExpense(expense)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View"
                            >
                              👁️
                            </button>
                            {hasPermission('expenses.update') && (
                            <button
                              onClick={() => handleEditRecurringExpense(expense)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            )}
                            {hasPermission('expenses.delete') && (
                            <button
                              onClick={() => handleDeleteRecurringExpense(expense.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              🗑️
                            </button>
                            )}
                            {(() => {
                              if (!expense.next_due_date) return null;
                              const today = new Date();
                              const dueDate = new Date(expense.next_due_date);
                              const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                              
                              if (diffDays <= 0) {
                                return (
                                  <button
                                    onClick={() => handleProcessIndividualExpense(expense.id)}
                                    className="text-purple-600 hover:text-purple-800"
                                    title="Process Due Expense"
                                  >
                                    Process
                                  </button>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'payroll' && (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gross Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deductions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payrollRecords.map(record => (
                  <tr key={record.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{record.member?.name || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{record.monthYear}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(record.grossSalary, organization?.currency_code)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(record.deductions, organization?.currency_code)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(record.netSalary, organization?.currency_code)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        record.status === 'paid' ? 'bg-green-100 text-green-800' :
                        record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {record.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'budget' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map(category => (
            <div key={category.id} className="bg-white p-6 rounded-lg shadow border">
              <h3 className="font-semibold text-gray-900">{category.name}</h3>
              <div className="mt-2">
                <div className="flex justify-between text-sm">
                  <span>Budget: {formatCurrency(category.budgetLimit, organization?.currency_code)}</span>
                  <span>Spent: {formatCurrency(0, organization?.currency_code)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div className="bg-blue-600 h-2 rounded-full" style={{width: '0%'}}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Expense Modal */}
      {viewingExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Expense Details</h3>
            <div className="space-y-3">
              <div><strong>Date:</strong> {formatDate(viewingExpense.expenseDate)}</div>
              <div><strong>Description:</strong> {viewingExpense.description}</div>
              <div><strong>Category:</strong> {viewingExpense.category?.name || 'N/A'}</div>
              <div><strong>Amount:</strong> {formatCurrency(viewingExpense.amount, organization?.currency_code)}</div>
              <div><strong>Type:</strong> {viewingExpense.expenseType}</div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setViewingExpense(null)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Recurring Expense Modal */}
      {viewingRecurringExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Recurring Expense Details</h3>
            <div className="space-y-3">
              <div><strong>Description:</strong> {viewingRecurringExpense.description}</div>
              <div><strong>Category:</strong> {viewingRecurringExpense.category?.name || 'N/A'}</div>
              <div><strong>Amount:</strong> {formatCurrency(viewingRecurringExpense.amount, organization?.currency_code)}</div>
              <div><strong>Frequency:</strong> {viewingRecurringExpense.frequency}</div>
              <div><strong>Next Due:</strong> {viewingRecurringExpense.next_due_date ? formatDate(viewingRecurringExpense.next_due_date).replace(/\s/g, '-') : 'N/A'}</div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setViewingRecurringExpense(null)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseManagement;